import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  User 
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';

import { Role } from './types';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// 1. Initialize Firebase App Singleton
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// 2. Initialize Firestore Singleton with Multi-Tab Persistence
let db: any;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
} catch (e) {
  db = getFirestore(app);
}

// 3. Initialize Auth Providers
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * 👤 User Profile Guard & Migration Engine
 * Ensures every user has a Firestore document structured to support:
 * 1. Personal Mode (Data nested in /users/{uid}/{collection})
 * 2. Household Mode (Data shared in /households/{hid}/{collection})
 * * NOTE: Uses atomic setDoc with merge:true to avoid "Missing Permissions" 
 * race conditions on initial login.
 */
export const ensureUserProfileDocument = async (user: User) => {
  if (!user.uid) return;

  const userRef = doc(db, 'users', user.uid);

  // 🎯 DATA PAYLOAD
  // Using setDoc with { merge: true } allows us to set defaults for new users
  // while only updating 'lastActive' for existing users.
  const profileData = {
    // Basic Identity (Updates if changed at Google/Auth level)
    id: user.uid,
    email: user.email || '',
    name: user.displayName || user.email?.split('@')[0] || 'New User',
    avatar: user.photoURL || undefined,
    lastActive: serverTimestamp(),

    // --- DEFAULT FIELDS (Only written if they don't exist) ---
    role: Role.MEMBER,
    acceptedLegalVersion: '',
    acceptedDisclaimerVersion: '',
    preferredLanguage: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    autoDetectTz: true,
    activeSparkCategories: [],
    dashboardLayout: [],
    activeHouseholdId: null, // Default to Personal Mode
    memberOf: [],           // Initialize empty household array
  };

  try {
    // We use setDoc + merge instead of getDoc + updateDoc to prevent 
    // "Missing Permissions" errors for existing users during the auth-sync window.
    await setDoc(userRef, profileData, { merge: true });
    
    // Legacy users created before 'createdAt' was added
    // We do this as a safe check without an explicit read
    await setDoc(userRef, { createdAt: serverTimestamp() }, { merge: true });
    
  } catch (error) {
    console.error("Critical: Profile sync failed. Verify Firestore Rules.", error);
    throw error; // Pass to App.tsx to handle initialization failure
  }
};

export {
  auth,
  db,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut
};